import React, { useEffect, useState } from 'react';
import { backupAPI } from '../lib/api';
import { fehlertext } from '../lib/fehlertext';
import { mbText, nachtFehlt, dateinameAus } from '../lib/backupAnzeige';
import { Button } from './ui/button';
import { CheckCircle2, AlertTriangle, CloudUpload, Download, Loader2, BellRing } from 'lucide-react';
import { toast } from 'sonner';

const zeit = (wert) => {
  if (!wert) return '—';
  try { return new Date(wert).toLocaleString('de-DE'); } catch (e) { return String(wert); }
};

export default function CloudSicherung() {
  const [stand, setStand] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const [ladeSchluessel, setLadeSchluessel] = useState(null);

  const laden = async () => {
    try {
      const antwort = await backupAPI.cloudStatus();
      setStand(antwort.data);
    } catch (err) {
      setStand({ eingerichtet: false, aktiv: false, fehlt: ['Status nicht abrufbar'] });
    }
  };

  useEffect(() => { laden(); }, []);

  const jetztSichern = async () => {
    setLaeuft(true);
    try {
      await backupAPI.cloudJetzt();
      toast.success('Sicherung in die Cloud erstellt');
      await laden();
    } catch (err) {
      toast.error(fehlertext(err, 'Die Sicherung konnte nicht erstellt werden'));
    } finally {
      setLaeuft(false);
    }
  };

  const testAlarm = async () => {
    setLaeuft(true);
    try {
      const antwort = await backupAPI.cloudTestAlarm();
      toast[antwort.data?.gesendet ? 'success' : 'error'](
        antwort.data?.gesendet ? 'Test-Alarm gesendet' : 'Test-Alarm wurde nicht gesendet');
    } catch (err) {
      toast.error(fehlertext(err, 'Test-Alarm nicht möglich'));
    } finally {
      setLaeuft(false);
    }
  };

  // Auftrag SP1: Speicher-Messungen der letzten 14 Tage als JSON-Datei
  const speicherProtokollLaden = async () => {
    try {
      const antwort = await backupAPI.speicherProtokoll();
      const url = window.URL.createObjectURL(
        new Blob([JSON.stringify(antwort.data, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'speicher-protokoll.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(fehlertext(err, 'Speicher-Protokoll konnte nicht geladen werden'));
    }
  };

  const herunterladen = async (schluessel) => {
    setLadeSchluessel(schluessel);
    try {
      const antwort = await backupAPI.cloudDownload(schluessel);
      const url = window.URL.createObjectURL(
        new Blob([antwort.data], { type: 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = dateinameAus(schluessel);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Wegen responseType 'blob' kommt auch der Fehlertext als Blob an.
      let meldung = null;
      if (err && err.response && err.response.data instanceof Blob) {
        try {
          meldung = JSON.parse(await err.response.data.text()).detail;
        } catch (e2) {
          meldung = null;
        }
      }
      toast.error(meldung || fehlertext(err, 'Die Sicherung konnte nicht geladen werden'));
    } finally {
      setLadeSchluessel(null);
    }
  };

  if (!stand) return null;

  if (!stand.eingerichtet) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700"
        data-testid="cloud-nicht-eingerichtet">
        <p className="font-semibold text-slate-800 mb-1">Automatische Sicherung (Hetzner, verschlüsselt)</p>
        Nicht eingerichtet – es fehlen: {(stand.fehlt || []).join(', ')}. (Nur im Live-System.)
      </div>
    );
  }

  const fehlerhaft = stand.status === 'fehler' || stand.status === 'verschoben';
  const nachtOffen = nachtFehlt(stand.letzte_nacht_datum, new Date());

  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="cloud-sicherung">
      <p className="font-semibold text-slate-800">Automatische Sicherung (Hetzner, verschlüsselt)</p>

      {!stand.aktiv ? (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="cloud-abgeschaltet">
          Eingerichtet, aber abgeschaltet (ENABLE_NIGHTLY_DB_BACKUP).
        </div>
      ) : (
        <div className="text-sm text-slate-700 space-y-1">
          <p className="flex items-center gap-2" data-testid="cloud-letzte-sicherung">
            {fehlerhaft || nachtOffen
              ? <AlertTriangle className="h-4 w-4 text-red-600" />
              : <CheckCircle2 className="h-4 w-4 text-green-600" />}
            <span>
              Letzte Sicherung: {zeit(stand.letzter_erfolg_at)} · {mbText(stand.letzte_groesse)} ·
              verschlüsselt{stand.geprueft ? ' · geprüft' : ''}
              {stand.loeschschutz ? ' · Löschschutz 30 Tage' : ''}
            </span>
          </p>
          {!stand.loeschschutz && stand.letzter_versuch_at && (
            <p className="text-amber-800" data-testid="cloud-ohne-loeschschutz">Löschschutz: aus</p>
          )}
          {fehlerhaft && (
            <p className="text-red-700" data-testid="cloud-fehler">
              Letzter Versuch fehlgeschlagen: {stand.letzter_fehler || 'unbekannt'}
              {stand.neuer_versuch_at ? ` · Neuer Versuch um ${zeit(stand.neuer_versuch_at)}` : ''}
            </p>
          )}
          {nachtOffen && (
            <p className="text-red-700 font-medium" data-testid="cloud-nacht-fehlt">
              Die Sicherung der letzten Nacht fehlt – bitte Dominik informieren.
            </p>
          )}
          <p>Nächste automatische Sicherung: {zeit(stand.naechster_lauf)}</p>
          <p>
            Aufbewahrung: {stand.aufbewahrung?.tage} Tage täglich, {stand.aufbewahrung?.monate} Monatsstände;
            danach endgültig gelöscht. Schlüssel-ID: {stand.schluessel_id}
          </p>
          {!stand.alarm_eingerichtet && (
            <p className="text-amber-800" data-testid="cloud-ohne-alarm">
              Keine Alarm-Adresse (BACKUP_ALARM_EMAIL) – Fehler werden nicht gemeldet.
            </p>
          )}
          {stand.letzte_entpackt_bytes >= 60 * 1024 * 1024 && (
            <p className="text-amber-800" data-testid="cloud-groesse">
              Die Sicherung ist entpackt {mbText(stand.letzte_entpackt_bytes)} groß (Grenze 200 MB) –
              bitte Dominik informieren.
            </p>
          )}
        </div>
      )}

      {stand.aktiv && stand.listen_fehler && (
        <p className="text-sm text-amber-800" data-testid="cloud-listen-fehler">
          Die Liste der Sicherungen konnte nicht geladen werden ({stand.listen_fehler}).
        </p>
      )}

      {stand.aktiv && (stand.sicherungen || []).length > 0 && (
        <div className="space-y-1" data-testid="cloud-liste">
          {(stand.sicherungen || []).map((s) => (
            <div key={s.schluessel} className="flex items-center justify-between gap-3 text-sm border-b border-slate-100 py-1">
              <span className="text-slate-700">
                {zeit(s.erstellt_at)} · {s.art === 'nacht' ? 'Nacht' : 'Hand'} · {mbText(s.groesse)}
                {s.gesperrt_bis ? ` · gesperrt bis ${zeit(s.gesperrt_bis)}` : ''}
              </span>
              <Button variant="ghost" size="sm" onClick={() => herunterladen(s.schluessel)}
                disabled={ladeSchluessel !== null}
                data-testid={`cloud-download-${s.schluessel}`}>
                {ladeSchluessel === s.schluessel
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <Download className="h-3.5 w-3.5 mr-1" />} Herunterladen
              </Button>
            </div>
          ))}
          <p className="text-xs text-slate-500 pt-1">
            Die Datei ist verschlüsselt. Zum Wiederherstellen unten bei „Backup wiederherstellen"
            auswählen und zuerst prüfen.
          </p>
        </div>
      )}

      {stand.aktiv && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={jetztSichern} disabled={laeuft}
            data-testid="cloud-jetzt-button">
            {laeuft ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CloudUpload className="h-4 w-4 mr-2" />}
            Jetzt in die Cloud sichern
          </Button>
          <Button variant="outline" size="sm" onClick={testAlarm} disabled={laeuft}
            data-testid="cloud-test-alarm-button">
            <BellRing className="h-4 w-4 mr-2" /> Test-Alarm senden
          </Button>
          <Button variant="outline" size="sm" onClick={speicherProtokollLaden} disabled={laeuft}
            data-testid="speicher-protokoll-button">
            <Download className="h-4 w-4 mr-2" /> Speicher-Protokoll herunterladen
          </Button>
        </div>
      )}
    </div>
  );
}
